/**
 * What a boundary form has to be before it is worth sending.
 *
 * Every rule here has a counterpart in the cluster, which refuses the same
 * declarations — the point of repeating them is *where* the refusal appears. A
 * cluster answers with a status and a sentence about the whole call; this
 * answers beside the box that has to change.
 */

/** The boxes a refusal can point at. */
export type ScopeField = 'prefix' | 'node' | 'kinds' | 'environment' | 'maxLive' | 'leaseSecs';

/** The sentences a check can answer with, as translation keys. */
export type ScopeCheckKey =
  | 'scopes.check.prefixRequired'
  | 'scopes.check.prefixShape'
  | 'scopes.check.nodeRequired'
  | 'scopes.check.kindsRequired'
  | 'scopes.check.environmentRequired'
  | 'scopes.check.noWhitespace'
  | 'scopes.check.maxLiveRange'
  | 'scopes.check.leaseRange';

/** What the form holds, as text, before anything is parsed. */
export interface ScopeForm {
  prefix: string;
  node: string;
  kinds: readonly string[];
  environment: string;
  maxLive: string;
  leaseSecs: string;
}

const WHITESPACE = /\s/;
const DIGITS = /^\d+$/;
/** What a resource id may carry, which is what a prefix of one may carry. */
const PREFIX = /^[A-Za-z0-9_.-]+$/;

export function checkScope(form: ScopeForm): Partial<Record<ScopeField, ScopeCheckKey>> {
  const errors: Partial<Record<ScopeField, ScopeCheckKey>> = {};

  const prefix = form.prefix.trim();
  if (prefix === '') errors.prefix = 'scopes.check.prefixRequired';
  else if (WHITESPACE.test(prefix) || !PREFIX.test(prefix))
    errors.prefix = 'scopes.check.prefixShape';

  if (form.node.trim() === '') errors.node = 'scopes.check.nodeRequired';

  if (form.kinds.length === 0) errors.kinds = 'scopes.check.kindsRequired';

  if (form.environment.trim() === '') errors.environment = 'scopes.check.environmentRequired';
  else if (WHITESPACE.test(form.environment.trim()))
    errors.environment = 'scopes.check.noWhitespace';

  const ceiling = form.maxLive.trim();
  if (!DIGITS.test(ceiling) || Number(ceiling) < 1) errors.maxLive = 'scopes.check.maxLiveRange';

  const lease = form.leaseSecs.trim();
  if (!DIGITS.test(lease) || Number(lease) < 1) errors.leaseSecs = 'scopes.check.leaseRange';

  return errors;
}
